import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Card, Alert, Badge, Spinner, OverlayTrigger, Tooltip, Tabs, Tab, SearchField } from '@/components/ui';
import type { Role } from '../../../types/Role';
import type { Permission } from '../../../types/User';
import type { Classification } from '../../../types/Employee';
import { classificationService } from '../../../services/classificationService';
import { rolesService } from '../../../services/rolesService';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import {
  displayModuleForPermission,
  HIRED_EQUIPMENT_MODULE,
  isHiredEquipmentFeaturePermission,
} from '../utils/permissionModules';
import { uiDash } from '../../../utils/uiText';
import {
  TRUECONTEXT_MODULE,
  WIDGETS_MODULE,
  permissionLabel,
  sortModuleNames,
  sortPermissions,
  legacyCodeLabel,
} from '../utils/permissionDisplay';

interface RoleModalProps {
  show: boolean;
  onHide: () => void;
  role: Role | null;
  permissions: Permission[];
  onSave: (roleData: Partial<Role>) => void;
}

const REQUIRED_WIDGET_PERMISSIONS: Array<
  Pick<Permission, 'key' | 'name' | 'description' | 'usage'>
> = [
  {
    key: 'widget:pending_approvals_bar',
    name: 'Pending Approvals per Submission Bar Graph',
    description: 'Allows viewing the Pending Approvals by Foreman Area bar chart on the Home page.',
    usage: 'Controls visibility of the Home page pending approvals bar chart widget.',
  },
  {
    key: 'widget:pending_approvals_kpi',
    name: 'Pending Approvals per Submission KPI',
    description: 'Allows viewing the Daily Log Submissions KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page pending approvals KPI widget.',
  },
  {
    key: 'widget:internal_review_kpi',
    name: 'Tasks in Internal Review KPI',
    description: 'Allows viewing the Internal Review KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page internal review KPI widget.',
  },
  {
    key: 'widget:active_tasks_kpi',
    name: 'Active Tasks KPI',
    description: 'Allows viewing active and related KPI widgets on the Home page.',
    usage: 'Controls visibility of Home page active/workload KPI widgets.',
  },
  {
    key: 'widget:overdue_tasks_kpi',
    name: 'Overdue Tasks KPI',
    description: 'Allows viewing the Overdue Tasks KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page overdue tasks KPI widget.',
  },
  {
    key: 'widget:planned_start_this_week_kpi',
    name: 'Tasks Planned Start Date This Week KPI',
    description: 'Allows viewing the Tasks Planned Start Date This Week KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page planned start this week KPI widget.',
  },
  {
    key: 'widget:marked_complete_kpi',
    name: 'Tasks Marked as Complete KPI',
    description: 'Allows viewing the marked-complete KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page marked complete KPI widget.',
  },
  {
    key: 'widget:completed_last_week_kpi',
    name: 'Tasks Completed Last Week KPI',
    description: 'Allows viewing the completed-last-week KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page completed last week KPI widget.',
  },
  {
    key: 'widget:completed_this_week_kpi',
    name: 'Tasks Completed This Week KPI',
    description: 'Allows viewing the completed-this-week KPI widget on the Home page.',
    usage: 'Controls visibility of the Home page completed this week KPI widget.',
  },
  {
    key: 'widget:insurance_expiries',
    name: 'Upcoming Insurance Expiries',
    description: 'Allows viewing the Vendor Compliance widget on the Home page.',
    usage: 'Shows vendor WCB, liability insurance and per-resource vehicle insurance expiring within 30 days, plus anything already lapsed.',
  },
];

/** Non-widget PCR permissions – grouped under PCR. */
const REQUIRED_PCR_PERMISSIONS: Array<
  Pick<Permission, 'key' | 'name' | 'description' | 'usage'>
> = [
  {
    key: 'submission:view',
    name: 'View Submissions',
    description: 'Allows viewing form submissions, daily logs, and the PCR submissions list.',
    usage:
      'Grants read-only access to the Form Submissions page and PCR list. Required to see existing PCR rows when the user has pcr:create.',
  },
  {
    key: 'submission:pcr_update',
    name: 'Update PCR Status',
    description:
      'Allows triaging public PCR workflow between New and Internal Review, editing intake fields, and notes – not Finalized (that requires pcr:finalize).',
    usage: 'PATCH /api/pcr/submissions/:id and /status for field edits and workflow changes.',
  },
  {
    key: 'submission:pcr_reply',
    name: 'Reply to PCR Submitter',
    description: 'Allows sending email replies to PCR submitters from WRAP.',
    usage: 'Required for POST reply and GET reply templates (not contact log viewing).',
  },
  {
    key: 'pcr:create',
    name: 'Create PCR',
    description: 'Allows manually creating Public Condition Report (PCR) submissions in WRAP.',
    usage: 'Enables the Create PCR action and POST /api/pcr/submissions.',
  },
  {
    key: 'pcr:history_view',
    name: 'View PCR Notes/History',
    description: 'Allows viewing PCR submission history and supervisor notes.',
    usage:
      'Authorizes GET history, GET contact-log, and the Notes & history UI (including Contact log tab).',
  },
  {
    key: 'pcr:history_create',
    name: 'Add PCR Notes',
    description: 'Allows adding supervisor notes to a PCR submission history.',
    usage: 'Authorizes POST /api/pcr/submissions/:id/history-comment.',
  },
  {
    key: 'pcr:internal_notify',
    name: 'Receive Internal PCR Email Notifications',
    description:
      'Allows receiving email when a new public PCR is ingested for foreman area(s) the user belongs to.',
    usage: 'Gates outbound internal staff notification emails for new public PCRs.',
  },
  {
    key: 'pcr:finalize',
    name: 'Finalize PCR',
    description:
      'Allows moving a PCR to Finalized (close without Work ID, bulk close, link/promote to Work ID, or status Finalized).',
    usage: 'Not granted with submission:pcr_update alone.',
  },
  {
    key: 'pcr:internal_review',
    name: 'Set PCR to Internal Review',
    description: 'Allows moving a public PCR to Internal Review status.',
    usage: 'PATCH status to internal_review and Move to Internal Review UI.',
  },
];

const PCR_PERMISSION_KEYS = new Set(REQUIRED_PCR_PERMISSIONS.map((p) => p.key));

/** Vendor-directory keys — always listed under Hired Equipment even before the Permission rows exist. */
const REQUIRED_HE_MASTER_PERMISSIONS: Array<
  Pick<Permission, 'key' | 'name' | 'description' | 'usage'>
> = [
  {
    key: 'heMaster:vendor_read',
    name: 'Read Vendors',
    description:
      'Allows reading active vendors and their rates for pickers, such as the hired equipment submission form.',
    usage:
      'Lets a hired equipment form choose a vendor. Does not open the Vendors admin screen or Approvals > Vendors.',
  },
  {
    key: 'heMaster:vendor_directory',
    name: 'View Vendors Across All Areas',
    description:
      'Allows seeing vendor and resource records in every service area, so a duplicate is not created of a subcontractor already set up elsewhere.',
    usage:
      'Widens GET /api/vendors and vendor detail beyond the caller\'s own service areas. Does not allow creating a vendor outside their area.',
  },
  {
    key: 'heMaster:vendor_add_area',
    name: 'Add Vendor to My Area',
    description:
      'Allows marking an existing vendor as available in one of the holder\'s own service areas.',
    usage:
      'Gates adding an existing vendor to the caller\'s area. Supervisors hold this without full vendor edit.',
  },
];

// Component for permission label with controlled tooltip
const PermissionLabelWithTooltip: React.FC<{ permission: Permission }> = ({ permission }) => {
  const [show, setShow] = useState(false);

  if (!permission.description && !permission.usage) {
    return <span>{permissionLabel(permission.name)}</span>;
  }

  return (
    <OverlayTrigger
      placement="top"
      show={show}
      overlay={
        <Tooltip 
          id={`permission-tooltip-modal-${permission.id}`} 
          style={{ maxWidth: '450px' }}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <PermissionTooltipContent permission={permission} />
        </Tooltip>
      }
      trigger={[]}
    >
      <span 
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {permissionLabel(permission.name)}
      </span>
    </OverlayTrigger>
  );
};

// Expandable tooltip content component for permissions
const PermissionTooltipContent: React.FC<{ permission: Permission }> = ({ permission }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!permission.description && !permission.usage) {
    return <div>No description available</div>;
  }

  return (
    <div style={{ textAlign: 'left' }}>
      {permission.description && (
        <div className="tw-mb-2">
          <strong>Description:</strong>
          <div style={{ fontSize: '0.9rem', marginTop: '4px' }}>{uiDash(permission.description)}</div>
        </div>
      )}
      {permission.usage && (
        <>
          {!isExpanded ? (
            <div>
              <Button
                variant="link"
                size="sm"
                className="tw-p-0 tw-text-white tw-underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
                style={{ fontSize: '0.85rem' }}
              >
                Show usage details...
              </Button>
            </div>
          ) : (
            <div>
              <div className="tw-mb-2">
                <strong>Usage:</strong>
                <div style={{ fontSize: '0.9rem', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                  {uiDash(permission.usage)}
                </div>
              </div>
              <Button
                variant="link"
                size="sm"
                className="tw-p-0 tw-text-white tw-underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                style={{ fontSize: '0.85rem' }}
              >
                Show less
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RoleModal: React.FC<RoleModalProps> = ({
  show,
  onHide,
  role,
  permissions,
  onSave
}) => {
  const { hasPermission } = usePermissions();
  const { currentUser, loading: authLoading } = useAuth();
  const isAdministrator = !authLoading && currentUser?.role?.name === 'Admin';

  const canManageClassifications = hasPermission('role:classifications_manage');
  const canEditPermissions = hasPermission('permission:edit');
  
  const [formData, setFormData] = useState({
    name: '',
    access: [] as string[],
    classificationIds: [] as number[],
    isRoadInspector: false,
    isBridgeInspector: false,
    isSupervisor: false,
    hasPurchaseRequisitionAccess: false,
    hasMechanicDailyLogAccess: false
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [loadingClassifications, setLoadingClassifications] = useState(false);
  const [activeTab, setActiveTab] = useState('permissions');
  /** V2-059: filter-as-you-type over the permission list; ticks are kept while filtered. */
  const [permissionSearch, setPermissionSearch] = useState('');
  const [expandedServiceAreas, setExpandedServiceAreas] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  type RoleTemplateMode = 'scratch' | 'based';
  const [roleTemplateMode, setRoleTemplateMode] = useState<RoleTemplateMode>('scratch');
  const [templateRoles, setTemplateRoles] = useState<Role[]>([]);
  const [templateRolesLoading, setTemplateRolesLoading] = useState(false);
  const [basedOnRoleId, setBasedOnRoleId] = useState<number | ''>('');

  // Derive permission keys from role.access or from RolePermission (so prod roles work when access is missing)
  const getRoleAccessKeys = (r: Role | null | undefined): string[] => {
    if (!r) return [];
    if (r.access && r.access.length > 0) return r.access;
    const fromRp = (r as { RolePermission?: { permission?: { key: string } }[] }).RolePermission
      ?.map((rp) => rp.permission?.key)
      .filter((k): k is string => !!k) ?? [];
    return fromRp;
  };

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        access: getRoleAccessKeys(role),
        classificationIds: role.roleClassifications?.map(rc => rc.classificationId) || [],
        isRoadInspector: role.isRoadInspector || false,
        isBridgeInspector: role.isBridgeInspector || false,
        isSupervisor: role.isSupervisor || false,
        hasPurchaseRequisitionAccess: role.hasPurchaseRequisitionAccess || false,
        hasMechanicDailyLogAccess: role.hasMechanicDailyLogAccess || false
      });
    } else {
      setFormData({
        name: '',
        access: [],
        classificationIds: [],
        isRoadInspector: false,
        isBridgeInspector: false,
        isSupervisor: false,
        hasPurchaseRequisitionAccess: false,
        hasMechanicDailyLogAccess: false
      });
    }
    setValidationError(null);
    setActiveTab('permissions');
    setExpandedModules(new Set()); // Collapse all permission modules by default
    setRoleTemplateMode('scratch');
    setBasedOnRoleId('');
    setTemplateRoles([]);
    setTemplateRolesLoading(false);
  }, [role, show]);

  useEffect(() => {
    let cancelled = false;

    const loadTemplateRoles = async () => {
      if (!show || role || !isAdministrator || roleTemplateMode !== 'based') return;

      setTemplateRolesLoading(true);
      try {
        const rolesData = await rolesService.getRoles(1, 1000, '');
        if (cancelled) return;
        setTemplateRoles(rolesData.roles || []);
      } catch (err) {
        console.error('Error loading template roles:', err);
        if (!cancelled) setTemplateRoles([]);
      } finally {
        if (!cancelled) setTemplateRolesLoading(false);
      }
    };

    // Only load when needed (switching to "Based on Existing Role")
    if (show && !role && isAdministrator && roleTemplateMode === 'based' && templateRoles.length === 0) {
      void loadTemplateRoles();
    }

    return () => {
      cancelled = true;
    };
  }, [show, role, isAdministrator, roleTemplateMode, templateRoles.length]);

  useEffect(() => {
    if (show) {
      loadClassifications();
      setPermissionSearch('');
    }
  }, [show]);

  const loadClassifications = async () => {
    setLoadingClassifications(true);
    try {
      const allClassifications = await classificationService.getAllClassifications();
      setClassifications(allClassifications);
    } catch (error) {
      console.error('Error loading classifications:', error);
    } finally {
      setLoadingClassifications(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setValidationError('Role name is required');
      return;
    }

    onSave(formData);
  };

  const handlePermissionChange = (permissionKey: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      access: checked 
        ? [...prev.access, permissionKey]
        : prev.access.filter(key => key !== permissionKey)
    }));
  };

  const applyScratchTemplate = () => {
    setFormData((prev) => ({
      ...prev,
      access: [],
      classificationIds: [],
      isRoadInspector: false,
      isBridgeInspector: false,
      isSupervisor: false,
      hasPurchaseRequisitionAccess: false,
      hasMechanicDailyLogAccess: false,
    }));
  };

  const handleRoleTemplateModeChange = (mode: RoleTemplateMode) => {
    setRoleTemplateMode(mode);

    // Both templates start from "no permissions" until the user selects a template role (for "based")
    if (mode === 'scratch') {
      setBasedOnRoleId('');
      applyScratchTemplate();
    } else {
      setBasedOnRoleId('');
      applyScratchTemplate();
    }
  };

  const handleBasedOnRoleChange = async (value: string) => {
    if (value === '') {
      setBasedOnRoleId('');
      applyScratchTemplate();
      return;
    }

    const id = parseInt(value);
    setBasedOnRoleId(id);

    try {
      const existingRole = await rolesService.getRole(id);
      const accessKeys = getRoleAccessKeys(existingRole);
      const classificationIds = existingRole.roleClassifications?.map(rc => rc.classificationId) || [];
      setFormData((prev) => ({
        ...prev,
        access: accessKeys,
        classificationIds,
        isRoadInspector: existingRole.isRoadInspector ?? false,
        isBridgeInspector: existingRole.isBridgeInspector ?? false,
        isSupervisor: existingRole.isSupervisor ?? false,
        hasPurchaseRequisitionAccess: existingRole.hasPurchaseRequisitionAccess ?? false,
        hasMechanicDailyLogAccess: existingRole.hasMechanicDailyLogAccess ?? false,
      }));
    } catch (err) {
      console.error('Error applying template role:', err);
      applyScratchTemplate();
    }
  };

  const groupPermissionsByModule = (sourcePermissions: Permission[]) => {
    return sourcePermissions.reduce((acc, permission) => {
      // V2-071: the same category names as Show Details, in the Standards vocabulary.
      const module = displayModuleForPermission(permission.key, permission.module);
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);
  };

  const handleSelectAllModule = (module: string, modulePermissions: Permission[]) => {
    const moduleKeys = modulePermissions.map(p => p.key);
    const allSelected = moduleKeys.every(key => formData.access.includes(key));
    
    if (allSelected) {
      // Deselect all permissions in this module
      setFormData(prev => ({
        ...prev,
        access: prev.access.filter(key => !moduleKeys.includes(key))
      }));
    } else {
      // Select all permissions in this module
      setFormData(prev => ({
        ...prev,
        access: Array.from(new Set([...prev.access, ...moduleKeys]))
      }));
    }
  };

  const isModuleFullySelected = (modulePermissions: Permission[]) => {
    return modulePermissions.every(permission => 
      formData.access.includes(permission.key)
    );
  };

  const isModulePartiallySelected = (modulePermissions: Permission[]) => {
    return modulePermissions.some(permission => 
      formData.access.includes(permission.key)
    ) && !isModuleFullySelected(modulePermissions);
  };

  const countSelectedInPermissionList = (perms: Permission[]) =>
    perms.filter((p) => formData.access.includes(p.key)).length;

  const isTrueContextFullySelected = () => {
    return formData.isRoadInspector &&
           formData.isBridgeInspector &&
           formData.isSupervisor &&
           formData.hasPurchaseRequisitionAccess &&
           formData.hasMechanicDailyLogAccess;
  };

  const isTrueContextPartiallySelected = () => {
    const selectedCount = [
      formData.isRoadInspector,
      formData.isBridgeInspector,
      formData.isSupervisor,
      formData.hasPurchaseRequisitionAccess,
      formData.hasMechanicDailyLogAccess
    ].filter(Boolean).length;
    return selectedCount > 0 && selectedCount < 5;
  };

  const handleSelectAllTrueContext = () => {
    const allSelected = isTrueContextFullySelected();
    setFormData(prev => ({
      ...prev,
      isRoadInspector: !allSelected,
      isBridgeInspector: !allSelected,
      isSupervisor: !allSelected,
      hasPurchaseRequisitionAccess: !allSelected,
      hasMechanicDailyLogAccess: !allSelected
    }));
  };

  const widgetPermissions = useMemo(() => {
    const byKey = new Map<string, Permission>();
    permissions
      .filter((p) => p.key.startsWith('widget:') || p.module === 'Widgets')
      .forEach((p) => byKey.set(p.key, p));

    for (let i = 0; i < REQUIRED_WIDGET_PERMISSIONS.length; i += 1) {
      const required = REQUIRED_WIDGET_PERMISSIONS[i];
      if (!byKey.has(required.key)) {
        byKey.set(required.key, {
          id: -(i + 1),
          key: required.key,
          name: required.name,
          module: 'Widgets',
          description: required.description,
          usage: required.usage,
        });
      }
    }

    return sortPermissions(Array.from(byKey.values()));
  }, [permissions]);

  const nonWidgetPermissions = useMemo(() => {
    const byKey = new Map<string, Permission>();
    permissions
      .filter((p) => !p.key.startsWith('widget:') && p.module !== 'Widgets')
      .forEach((p) => byKey.set(p.key, p));

    for (let i = 0; i < REQUIRED_PCR_PERMISSIONS.length; i += 1) {
      const required = REQUIRED_PCR_PERMISSIONS[i];
      const existing = byKey.get(required.key) ?? permissions.find((p) => p.key === required.key);
      byKey.set(required.key, {
        id: existing?.id ?? -(1000 + i),
        key: required.key,
        name: required.name,
        module: 'PCR',
        description: required.description,
        usage: required.usage,
      });
    }

    for (let i = 0; i < REQUIRED_HE_MASTER_PERMISSIONS.length; i += 1) {
      const required = REQUIRED_HE_MASTER_PERMISSIONS[i];
      const existing = byKey.get(required.key) ?? permissions.find((p) => p.key === required.key);
      byKey.set(required.key, {
        id: existing?.id ?? -(2000 + i),
        key: required.key,
        name: required.name,
        module: HIRED_EQUIPMENT_MODULE,
        description: required.description,
        usage: required.usage,
      });
    }

    return Array.from(byKey.values());
  }, [permissions]);

  const groupedPermissions = useMemo(() => {
    const pcrModule: Permission[] = [];
    const hiredEquipmentModule: Permission[] = [];
    const other: Permission[] = [];

    for (const permission of nonWidgetPermissions) {
      if (PCR_PERMISSION_KEYS.has(permission.key)) {
        pcrModule.push({ ...permission, module: 'PCR' });
      } else if (isHiredEquipmentFeaturePermission(permission.key)) {
        hiredEquipmentModule.push({ ...permission, module: HIRED_EQUIPMENT_MODULE });
      } else {
        other.push(permission);
      }
    }

    const grouped = groupPermissionsByModule(other);
    if (pcrModule.length > 0) {
      grouped.PCR = pcrModule;
    }
    if (hiredEquipmentModule.length > 0) {
      grouped[HIRED_EQUIPMENT_MODULE] = hiredEquipmentModule;
    }
    // Permissions A–Z within every category (W-L1).
    for (const module of Object.keys(grouped)) {
      grouped[module] = sortPermissions(grouped[module]);
    }
    return grouped;
  }, [nonWidgetPermissions]);

  /**
   * Every category - widgets, the permission modules and TrueContext - in one A–Z list (W-L1).
   * Widgets used to be pinned first and PCR / Hired Equipment / TrueContext appended at the end.
   */
  const orderedModules = useMemo(() => {
    const names = Object.keys(groupedPermissions).filter((m) => m !== WIDGETS_MODULE);
    if (widgetPermissions.length > 0) names.push(WIDGETS_MODULE);
    names.push(TRUECONTEXT_MODULE);
    return sortModuleNames(names);
  }, [groupedPermissions, widgetPermissions]);

  /**
   * Access keys the role holds that no permission row describes (legacy codes). They are counted
   * in the summary, so they are listed - read-only - rather than being invisible here.
   */
  const legacyAccessKeys = useMemo(() => {
    const known = new Set([...nonWidgetPermissions, ...widgetPermissions].map((p) => p.key));
    return formData.access.filter((k) => !known.has(k)).sort();
  }, [formData.access, nonWidgetPermissions, widgetPermissions]);

  const trueContextSelectedCount = [
    formData.isRoadInspector,
    formData.isBridgeInspector,
    formData.isSupervisor,
    formData.hasPurchaseRequisitionAccess,
    formData.hasMechanicDailyLogAccess,
  ].filter(Boolean).length;

  const selectedPermissionCount = new Set(formData.access).size + trueContextSelectedCount;
  const allVisiblePermissionsCount = nonWidgetPermissions.length + widgetPermissions.length + 5;

  const groupClassificationsByServiceArea = () => {
    return classifications.reduce((acc, classification) => {
      const serviceAreaName = classification.serviceArea?.name || 'Unknown';
      if (!acc[serviceAreaName]) {
        acc[serviceAreaName] = [];
      }
      acc[serviceAreaName].push(classification);
      return acc;
    }, {} as Record<string, Classification[]>);
  };

  const handleClassificationChange = (classificationId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      classificationIds: checked
        ? [...prev.classificationIds, classificationId]
        : prev.classificationIds.filter(id => id !== classificationId)
    }));
  };

  const handleSelectAllServiceArea = (serviceArea: string, areaClassifications: Classification[]) => {
    const areaIds = areaClassifications.map(c => c.id);
    const allSelected = areaIds.every(id => formData.classificationIds.includes(id));
    
    if (allSelected) {
      // Deselect all classifications in this service area
      setFormData(prev => ({
        ...prev,
        classificationIds: prev.classificationIds.filter(id => !areaIds.includes(id))
      }));
    } else {
      // Select all classifications in this service area
      setFormData(prev => ({
        ...prev,
        classificationIds: Array.from(new Set([...prev.classificationIds, ...areaIds]))
      }));
    }
  };

  const isServiceAreaFullySelected = (areaClassifications: Classification[]) => {
    return areaClassifications.every(classification => 
      formData.classificationIds.includes(classification.id)
    );
  };

  const isServiceAreaPartiallySelected = (areaClassifications: Classification[]) => {
    return areaClassifications.some(classification => 
      formData.classificationIds.includes(classification.id)
    ) && !isServiceAreaFullySelected(areaClassifications);
  };

  const groupedClassifications = groupClassificationsByServiceArea();

  const toggleServiceArea = (serviceArea: string) => {
    setExpandedServiceAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceArea)) {
        newSet.delete(serviceArea);
      } else {
        newSet.add(serviceArea);
      }
      return newSet;
    });
  };

  const isServiceAreaExpanded = (serviceArea: string) => {
    return expandedServiceAreas.has(serviceArea);
  };

  const toggleModule = (module: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(module)) {
        newSet.delete(module);
      } else {
        newSet.add(module);
      }
      return newSet;
    });
  };

  const searchNeedle = permissionSearch.trim().toLowerCase();
  const searching = searchNeedle.length > 0;

  /** V2-059: a permission matches on its name or key ("vendor" finds the vendor permissions). */
  const permissionMatches = (permission: Permission) =>
    !searching ||
    (permission.name ?? '').toLowerCase().includes(searchNeedle) ||
    permission.key.toLowerCase().includes(searchNeedle);

  const TRUECONTEXT_LABELS = [
    'TrueContext Configuration',
    'Road Inspector',
    'Bridge Inspector',
    'Supervisor',
    'Purchase Requisition Access',
    'Mechanic Daily Log Access',
  ];
  const trueContextMatches = !searching || TRUECONTEXT_LABELS.some((l) => l.toLowerCase().includes(searchNeedle));

  // Every matching category opens while searching, so the matches are on screen.
  const isModuleExpanded = (module: string) => {
    return searching || expandedModules.has(module);
  };

  return (
    <>
      {/* Width comes from `size="xl"` (~900px, and responsive) — the old injected
          `min-width: 900px !important` block would overflow phones. Long permission
          labels still wrap via the body class below. */}
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        lockDocument={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{role ? 'Edit Role' : 'Add Role'}</Modal.Title>
        </Modal.Header>
      {/* The form sits between the panel and Modal.Body, so it must carry the flex-column
          contract itself. Without `flex min-h-0 flex-1 flex-col` the panel's bounded height
          stops propagating, Body grows to its full content height instead of scrolling, and
          the footer (Cancel / Update Role) is clipped out of reach by the panel's overflow. */}
      <Form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        {/* Height/scrolling is handled by the Modal primitive (bounded flex column). */}
        <Modal.Body className="tw-overflow-x-hidden [&_.form-check-label]:tw-break-words">
          {validationError && (
            <Alert variant="danger">{validationError}</Alert>
          )}
          
          <Form.Group className="tw-mb-3">
            <Form.Label>Role Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                name: e.target.value
              }))}
              placeholder="Enter role name"
              required
            />
          </Form.Group>

          {!role && isAdministrator && (
            <div className="tw-mb-3">
              <Form.Label className="tw-mb-2">Role Permission Template</Form.Label>

              <div className="tw-flex tw-flex-col tw-gap-2">
                <Form.Check
                  type="radio"
                  name="role-template"
                  id="role-template-scratch"
                  label="From Scratch"
                  checked={roleTemplateMode === 'scratch'}
                  onChange={() => handleRoleTemplateModeChange('scratch')}
                />
                <Form.Check
                  type="radio"
                  name="role-template"
                  id="role-template-based"
                  label="Based on Existing Role"
                  checked={roleTemplateMode === 'based'}
                  onChange={() => handleRoleTemplateModeChange('based')}
                />
              </div>

              {roleTemplateMode === 'based' && (
                <div className="tw-mt-3">
                  <Form.Label className="tw-text-content-muted">Choose an existing role to clone permissions from</Form.Label>
                  {templateRolesLoading ? (
                    <div className="tw-text-content-muted tw-mt-2">Loading roles...</div>
                  ) : (
                    <Form.Select
                      className="tw-mt-2"
                      value={basedOnRoleId === '' ? '' : basedOnRoleId.toString()}
                      onChange={(e) => void handleBasedOnRoleChange(e.target.value)}
                    >
                      <option value="">Select an existing role...</option>
                      {templateRoles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </div>
              )}
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => k && setActiveTab(k)}
            className="tw-mb-3"
          >
            <Tab eventKey="permissions" title="Permissions">
              {!canEditPermissions && (
                <Alert variant="info" className="tw-mb-3">
                  <i className="bi bi-info-circle tw-mr-2"></i>
                  You do not have permission to edit role permissions. You can view permissions but cannot modify them. Contact an administrator to change role permissions.
                </Alert>
              )}
              <Form.Group className="tw-mb-3">
                <Form.Label>Permissions</Form.Label>
                <SearchField
                  className="tw-mb-3"
                  value={permissionSearch}
                  onChange={setPermissionSearch}
                  onSearch={setPermissionSearch}
                  placeholder="Permission name or key"
                  label="Search permissions"
                />
                <div>
                  {searching &&
                    !trueContextMatches &&
                    orderedModules.every(
                      (m) =>
                        m === TRUECONTEXT_MODULE ||
                        !(m === WIDGETS_MODULE ? widgetPermissions : groupedPermissions[m] ?? []).some(permissionMatches),
                    ) && (
                      <p className="tw-text-content-muted" role="status">
                        No permissions match “{permissionSearch.trim()}”.
                      </p>
                    )}
                  {/* Every category A–Z, Widgets and TrueContext included (W-L1). */}
                  {orderedModules.map((module) => {
                    if (module === TRUECONTEXT_MODULE) {
                      if (!trueContextMatches) return null;
                      return (
                  <Card key={module} className="tw-mb-3">
                    <Card.Header
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleModule('TrueContext Configuration')}
                    >
                      <div className="tw-flex tw-justify-between tw-items-center">
                        <div className="tw-flex tw-items-center">
                          <i
                            className={`bi bi-chevron-${isModuleExpanded('TrueContext Configuration') ? 'down' : 'right'} tw-mr-2`}
                            style={{ fontSize: '0.8rem' }}
                          ></i>
                          <h6 className="tw-mb-0">TrueContext Configuration</h6>
                          <Badge bg="secondary" className="tw-ml-2 tw-whitespace-nowrap">
                            {trueContextSelectedCount}/5
                          </Badge>
                        </div>
                        <Form.Check
                          type="checkbox"
                          label="Select All"
                          checked={isTrueContextFullySelected()}
                          disabled={!canEditPermissions}
                          ref={(input: HTMLInputElement | null) => {
                            if (input) {
                              input.indeterminate = isTrueContextPartiallySelected();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (canEditPermissions) {
                              handleSelectAllTrueContext();
                            }
                          }}
                        />
                      </div>
                    </Card.Header>
                    {isModuleExpanded('TrueContext Configuration') && (
                      <Card.Body>
                        <Form.Text className="tw-text-content-muted tw-block tw-mb-3">
                          Configure TrueContext settings for this role. These settings control access to TrueContext features for all users with this role.
                        </Form.Text>
                        <Row>
                          <Col md={6} className="tw-mb-2">
                            <Form.Check
                              type="checkbox"
                              id="isRoadInspector"
                              label="Road Inspector"
                              checked={formData.isRoadInspector}
                              onChange={e => canEditPermissions && setFormData({...formData, isRoadInspector: e.target.checked})}
                              disabled={!canEditPermissions}
                            />
                          </Col>
                          <Col md={6} className="tw-mb-2">
                            <Form.Check
                              type="checkbox"
                              id="isBridgeInspector"
                              label="Bridge Inspector"
                              checked={formData.isBridgeInspector}
                              onChange={e => canEditPermissions && setFormData({...formData, isBridgeInspector: e.target.checked})}
                              disabled={!canEditPermissions}
                            />
                          </Col>
                          <Col md={6} className="tw-mb-2">
                            <Form.Check
                              type="checkbox"
                              id="isSupervisor"
                              label="Supervisor"
                              checked={formData.isSupervisor}
                              onChange={e => canEditPermissions && setFormData({...formData, isSupervisor: e.target.checked})}
                              disabled={!canEditPermissions}
                            />
                          </Col>
                          <Col md={6} className="tw-mb-2">
                            <Form.Check
                              type="checkbox"
                              id="hasPurchaseRequisitionAccess"
                              label="Purchase Requisition Access"
                              checked={formData.hasPurchaseRequisitionAccess}
                              onChange={e => canEditPermissions && setFormData({...formData, hasPurchaseRequisitionAccess: e.target.checked})}
                              disabled={!canEditPermissions}
                            />
                          </Col>
                          <Col md={6} className="tw-mb-2">
                            <Form.Check
                              type="checkbox"
                              id="hasMechanicDailyLogAccess"
                              label="Mechanic Daily Log Access"
                              checked={formData.hasMechanicDailyLogAccess}
                              onChange={e => canEditPermissions && setFormData({...formData, hasMechanicDailyLogAccess: e.target.checked})}
                              disabled={!canEditPermissions}
                            />
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>
                      );
                    }
                    // V2-059: while searching, a category lists only its matches and Select All
                    // applies to those visible items only.
                    const modulePermissions = (
                      module === WIDGETS_MODULE ? widgetPermissions : groupedPermissions[module] ?? []
                    ).filter(permissionMatches);
                    if (searching && modulePermissions.length === 0) return null;
                    const isExpanded = isModuleExpanded(module);
                    return (
                      <Card key={module} className="tw-mb-3">
                        <Card.Header
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleModule(module)}
                        >
                          <div className="tw-flex tw-justify-between tw-items-center">
                            <div className="tw-flex tw-items-center">
                              <i
                                className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} tw-mr-2`}
                                style={{ fontSize: '0.8rem' }}
                              ></i>
                              <h6 className="tw-mb-0">{module}</h6>
                              <Badge bg="secondary" className="tw-ml-2 tw-whitespace-nowrap">
                                {countSelectedInPermissionList(modulePermissions)}/{modulePermissions.length}
                              </Badge>
                            </div>
                            <Form.Check
                              type="checkbox"
                              label="Select All"
                              checked={isModuleFullySelected(modulePermissions)}
                              disabled={!canEditPermissions}
                              ref={(input: HTMLInputElement | null) => {
                                if (input) {
                                  input.indeterminate = isModulePartiallySelected(modulePermissions);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (canEditPermissions) {
                                  handleSelectAllModule(module, modulePermissions);
                                }
                              }}
                            />
                          </div>
                        </Card.Header>
                        {isExpanded && (
                          <Card.Body>
                            <Row>
                              {modulePermissions.map(permission => (
                                <Col md={6} key={permission.key} className="tw-mb-2">
                                  <Form.Check
                                    type="checkbox"
                                    id={`permission-${permission.key}`}
                                    label={<PermissionLabelWithTooltip permission={permission} />}
                                    checked={formData.access.includes(permission.key)}
                                    disabled={!canEditPermissions}
                                    onChange={(e) => canEditPermissions && handlePermissionChange(permission.key, e.target.checked)}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </Card.Body>
                        )}
                      </Card>
                    );
                  })}
                  {legacyAccessKeys.length > 0 && (
                    <Card className="tw-mb-3" data-testid="legacy-access-codes">
                      <Card.Body>
                        <h6 className="tw-mb-1">Legacy Codes</h6>
                        <p className="tw-text-content-muted tw-text-sm tw-mb-2">
                          This role holds access codes that no longer match a permission. They are kept as they are.
                        </p>
                        <div className="tw-flex tw-flex-wrap tw-gap-1">
                          {legacyAccessKeys.map((k) => (
                            <Badge key={k} bg="light" text="dark">{legacyCodeLabel(k)}</Badge>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                </div>
                <div className="tw-mt-3 tw-p-3 tw-bg-surface-alt tw-rounded">
                  <small className="tw-text-content-muted" data-testid="selected-permissions-summary">
                    <strong>Selected Permissions:</strong> {selectedPermissionCount} of {allVisiblePermissionsCount}
                  </small>
                </div>
              </Form.Group>
            </Tab>

            <Tab eventKey="classifications" title="Classifications">
              {!canManageClassifications && (
                <Alert variant="info" className="tw-mb-3">
                  <i className="bi bi-info-circle tw-mr-2"></i>
                  You do not have permission to manage role classifications. Contact an administrator to modify role-classification assignments.
                </Alert>
              )}
              <Form.Group className="tw-mb-3">
                <Form.Label>Role Classifications</Form.Label>
                <Form.Text className="tw-text-content-muted tw-block tw-mb-3">
                  Select classifications that can be assigned to users with this role. Classifications are grouped by service area.
                  Users will only have access to classifications from their home service area.
                </Form.Text>
                {loadingClassifications ? (
                  <div className="tw-text-center tw-py-4">
                    <Spinner animation="border" size="sm">Loading...</Spinner>
                  </div>
                ) : (
                  <div>
                    {Object.entries(groupedClassifications).map(([serviceArea, areaClassifications]) => {
                      const isExpanded = isServiceAreaExpanded(serviceArea);
                      return (
                        <Card key={serviceArea} className="tw-mb-3">
                          <Card.Header
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleServiceArea(serviceArea)}
                          >
                            <div className="tw-flex tw-justify-between tw-items-center">
                              <div className="tw-flex tw-items-center">
                                <i
                                  className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} tw-mr-2`}
                                  style={{ fontSize: '0.8rem' }}
                                ></i>
                                <h6 className="tw-mb-0">{serviceArea}</h6>
                                <Badge bg="secondary" className="tw-ml-2">
                                  {areaClassifications.length}
                                </Badge>
                              </div>
                              <Form.Check
                                type="checkbox"
                                label="Select All"
                                checked={isServiceAreaFullySelected(areaClassifications)}
                                disabled={!canManageClassifications}
                                ref={(input: HTMLInputElement | null) => {
                                  if (input) {
                                    input.indeterminate = isServiceAreaPartiallySelected(areaClassifications);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (canManageClassifications) {
                                    handleSelectAllServiceArea(serviceArea, areaClassifications);
                                  }
                                }}
                              />
                            </div>
                          </Card.Header>
                          {isExpanded && (
                            <Card.Body>
                              <Row>
                                {areaClassifications.map(classification => (
                                  <Col md={6} key={classification.id} className="tw-mb-2">
                                    <Form.Check
                                      type="checkbox"
                                      id={`classification-${classification.id}`}
                                      label={
                                        <span>
                                          <strong>{classification.code}</strong>
                                          {classification.rate !== undefined && (
                                            <span className="tw-text-content-muted tw-ml-2">(${classification.rate.toFixed(2)})</span>
                                          )}
                                        </span>
                                      }
                                      checked={formData.classificationIds.includes(classification.id)}
                                      disabled={!canManageClassifications}
                                      onChange={(e) => canManageClassifications && handleClassificationChange(classification.id, e.target.checked)}
                                    />
                                  </Col>
                                ))}
                              </Row>
                            </Card.Body>
                          )}
                        </Card>
                      );
                    })}
                    {Object.keys(groupedClassifications).length === 0 && (
                      <div className="tw-text-center tw-py-4 tw-text-content-muted">
                        No classifications available
                      </div>
                    )}
                  </div>
                )}
                <div className="tw-mt-3 tw-p-3 tw-bg-surface-alt tw-rounded">
                  <small className="tw-text-content-muted">
                    <strong>Selected Classifications:</strong> {formData.classificationIds.length} of {classifications.length}
                  </small>
                </div>
              </Form.Group>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {role ? 'Update Role' : 'Add Role'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
    </>
  );
};

export default RoleModal;